<div class="panel">
  <h3 class="panel-title">Campaign Form</h3>

  <!-- Core fields -->
  <ng-container *ngIf="state.form() as form">
    <label class="field-label">Campaign Name</label>
    <input class="field-input" type="text" [ngModel]="form.campaign_name"
      (ngModelChange)="updateField('campaign_name', $event)" />

    <label class="field-label">Background</label>
    <textarea class="field-textarea" rows="3" [ngModel]="form.background"
      (ngModelChange)="updateField('background', $event)"></textarea>

    <label class="field-label">Business Goal</label>
    <textarea class="field-textarea" rows="3" [ngModel]="form.business_goal"
      (ngModelChange)="updateField('business_goal', $event)"></textarea>

    <label class="field-label">Communications Outcome</label>
    <textarea class="field-textarea" rows="3" [ngModel]="form.communications_outcome"
      (ngModelChange)="updateField('communications_outcome', $event)"></textarea>

    <label class="field-label">Key Messages</label>
    <textarea class="field-textarea" rows="3" [ngModel]="form.key_messages"
      (ngModelChange)="updateField('key_messages', $event)"></textarea>

    <label class="field-label">Tactics</label>
    <textarea class="field-textarea" rows="3" [ngModel]="form.tactics"
      (ngModelChange)="updateField('tactics', $event)"></textarea>

    <label class="field-label">Audience Message</label>
    <textarea class="field-textarea" rows="4" [ngModel]="form.audience_message"
      (ngModelChange)="updateField('audience_message', $event)"></textarea>

    <div class="two-col">
      <div>
        <label class="field-label">Priority</label>
        <select class="field-select" [ngModel]="form.priority" (ngModelChange)="updateField('priority', $event)">
          <option *ngFor="let p of priorities" [value]="p">{{ p }}</option>
        </select>
      </div>
      <div>
        <label class="field-label">Channel</label>
        <select class="field-select" [ngModel]="form.channel" (ngModelChange)="updateField('channel', $event)">
          <option *ngFor="let c of channels" [value]="c">{{ c }}</option>
        </select>
      </div>
    </div>

    <!-- Schedule -->
    <label class="checkbox-row">
      <input type="checkbox" [ngModel]="schedule" (ngModelChange)="onScheduleToggle($event)" />
      Schedule for a specific date/time
    </label>
    <p class="hint-text" *ngIf="!schedule">Leave unchecked to send as soon as the campaign is approved.</p>

    <div class="two-col" *ngIf="schedule">
      <div>
        <label class="field-label">Send Date</label>
        <input class="field-input" type="date" [(ngModel)]="sendDate" (ngModelChange)="updateSendDatetime()" />
      </div>
      <div>
        <label class="field-label">Send Time</label>
        <input class="field-input" type="time" [(ngModel)]="sendTime" (ngModelChange)="updateSendDatetime()" />
      </div>
    </div>

    <!-- Reminders -->
    <label class="checkbox-row">
      <input type="checkbox" [ngModel]="remindersOn" (ngModelChange)="onRemindersToggle($event)" />
      Set up reminders for this campaign
    </label>
    <p class="hint-text" *ngIf="!remindersOn">No follow-up reminders will be sent.</p>

    <ng-container *ngIf="remindersOn">
      <label class="field-label">How many reminders?</label>
      <select class="field-select" [ngModel]="reminderRows.length" (ngModelChange)="onReminderCountChange($event)">
        <option *ngFor="let n of reminderCounts" [ngValue]="n">{{ n }}</option>
      </select>

      <label class="field-label">Time zone</label>
      <app-timezone-picker [selected]="reminderTimezone"
        (selectedChange)="onReminderTimezoneChange($event)"></app-timezone-picker>

      <p class="hint-text">Defaults to consecutive days at the current time — edit any row freely.</p>

      <div class="reminder-row" *ngFor="let row of reminderRows; let i = index">
        <span class="reminder-badge">{{ i + 1 }}</span>
        <input class="field-input" type="date" [ngModel]="row.date"
          (ngModelChange)="updateReminderRow(i, 'date', $event)" />
        <input class="field-input" type="time" [ngModel]="row.time"
          (ngModelChange)="updateReminderRow(i, 'time', $event)" />
        <span class="reminder-preview" *ngIf="reminderPreview(row) as preview">{{ preview }}</span>
      </div>
      <div class="alert reminder-warning" role="note">
        Reminders must be scheduled at least 5 minutes after the initial sendout time.
      </div>
    </ng-container>

    <label class="field-label">Approver</label>
    <app-approver-picker [approverEmail]="state.form().approver"
      (approverEmailChange)="onApproverChange($event)"></app-approver-picker>

    <hr class="divider" />
    <h4 class="section-title">👥 Audience</h4>

    <label class="field-label">Locations</label>
    <app-multi-select-dropdown [options]="state.locationsOptions()" [selected]="form.audience.locations"
      placeholder="Select locations…"
      (selectedChange)="updateAudienceField('locations', $event)"></app-multi-select-dropdown>

    <label class="field-label">Teams</label>
    <app-multi-select-dropdown [options]="state.teamsOptions()" [selected]="form.audience.teams"
      placeholder="Select teams…" (selectedChange)="updateAudienceField('teams', $event)"></app-multi-select-dropdown>

    <label class="copilot-toggle-row">
      <input class="copilot-toggle" type="checkbox" role="switch" [ngModel]="form.audience.copilot_users_only"
        (ngModelChange)="updateAudienceField('copilot_users_only', $event)" [disabled]="sending" />
      <span>Send to CoPilot users only</span>
      <span class="copilot-toggle-state" aria-hidden="true">{{ form.audience.copilot_users_only ? 'ON' : 'OFF' }}</span>
    </label>

    <div class="two-col people-cols">
      <app-people-picker label="Inclusions" mode="include" [current]="form.audience.inclusions"
        [checkAgainstEmails]="baseAudienceEmails()"></app-people-picker>
      <app-people-picker label="Exclusions" mode="exclude" [current]="form.audience.exclusions"
        [checkAgainstEmails]="exclusionCheckEmails()"></app-people-picker>
    </div>

    <hr class="divider" />

    <div class="two-col">
      <button class="secondary-btn" (click)="peekAudience()" [disabled]="resolving || sending">
        <ng-container *ngIf="resolving">⏳ Resolving…</ng-container>
        <ng-container *ngIf="!resolving">
          <img class="btn-icon" src="assets/images/search.png" alt="" />
          Validate Audience
        </ng-container>
      </button>
      <button class="primary-btn" (click)="sendCampaign()" [disabled]="sending || resolving">
        {{ sending ? '⏳ Sending…' : 'Send' }}
      </button>
    </div>
    <div class="alert error" *ngIf="resolveError">{{ resolveError }}</div>
    <div class="alert error" *ngIf="sendError">{{ sendError }}</div>
    <div class="alert success" *ngIf="sendSuccess">{{ sendSuccess }}</div>

    <app-audience-dialog [result]="state.peekResults()" (closed)="closePeek()"></app-audience-dialog>
  </ng-container>
</div>